import { useNavigate } from "react-router-dom";
import { Button, CopyableCode } from "@satyrsmc/shared/components";
import { PaymentMethod } from "@satyrsmc/shared/client";
import type { MemberEventDetail } from "@satyrsmc/shared/dto/member/event";
import { resolveEventCost } from "@satyrsmc/shared/lib/event-pricing";
import { useWizard } from "./WizardContext";
import { useAuth } from "@/state/AuthContext";
import { formatDate } from "@/lib/date-utils";

const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  [PaymentMethod.Check]: "Check",
  [PaymentMethod.Cash]: "Cash",
  [PaymentMethod.Zelle]: "Zelle",
  [PaymentMethod.CreditCard]: "Credit Card",
};

interface Props {
  event: MemberEventDetail;
  onRetry?: () => void;
  retryError?: string | null;
  submitting?: boolean;
}

export function Step4Confirmation({ event, onRetry, retryError, submitting }: Props) {
  const { state } = useWizard();
  const { isMember } = useAuth();
  const navigate = useNavigate();
  const method = state.paymentMethod;

  const effectiveCost = resolveEventCost(event, isMember, state.attendance.kind === "single");

  const isPaidEvent = effectiveCost != null && effectiveCost > 0;

  const zelleAmount =
    method === PaymentMethod.Zelle && effectiveCost != null ? effectiveCost.toFixed(2) : null;

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-card p-4 space-y-3">
        <p className="text-sm font-medium">{event.name}</p>
        {method && (
          <p className="text-sm text-muted-foreground">
            Payment method: {PAYMENT_METHOD_LABELS[method]}
          </p>
        )}
        {event.refund_cutoff_date && (
          <p className="text-sm text-muted-foreground">
            RSVPs cannot be cancelled after {formatDate(event.refund_cutoff_date)}.
          </p>
        )}
      </div>

      {isPaidEvent && method && method !== PaymentMethod.CreditCard && (
        <div className="rounded-md border border-border bg-muted/50 p-4 space-y-2 text-sm">
          <p className="font-medium">Payment instructions</p>
          {method === PaymentMethod.Check || method === PaymentMethod.Cash ? (
            <>
              <p className="text-muted-foreground">
                Make checks payable to:{" "}
                <strong>
                  <em>Satyrs M/C</em>
                </strong>
              </p>
              <p className="text-muted-foreground">
                Send payment to:
                <address>
                  <strong>
                    Satyrs M/C
                    <br />
                    P.O. Box 1137
                    <br />
                    Los Angeles, CA 90078-1137
                  </strong>
                </address>
              </p>
            </>
          ) : method === PaymentMethod.Zelle ? (
            <p className="text-muted-foreground">
              Send{zelleAmount ? ` $${zelleAmount}` : ""} via Zelle to{" "}
              <CopyableCode text="Treasurer@SatyrsMC.org" />.
            </p>
          ) : null}
        </div>
      )}

      {retryError && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive space-y-2">
          <p>{retryError}</p>
          {onRetry && (
            <Button size="sm" variant="outline" onClick={onRetry} disabled={submitting}>
              {submitting ? "Retrying…" : "Retry"}
            </Button>
          )}
        </div>
      )}

      {!retryError && (
        <div className="rounded-md border border-border bg-muted/50 p-4 text-sm text-muted-foreground">
          {method === PaymentMethod.CreditCard
            ? "You're all set! Payment confirmed."
            : isPaidEvent
              ? "You're registered. Please complete payment using the instructions above."
              : "You're all set!"}
        </div>
      )}

      <Button
        className="w-full"
        onClick={() => navigate(`/events/${event.id}`)}
        disabled={submitting}
      >
        Done
      </Button>
    </div>
  );
}
